import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink } from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { INVESTMENT_FIELDS, fmt, fmtAxis, tooltipStyle } from "./oversiktConstants";
import { ShareTable, ShareCompareChart } from "./ShareCompare";
import { dec } from "@/lib/formatters";

const pct = (value, total) => (total && value > 0 ? `${dec(((value / total) * 100), 1)} %` : "—");

// Generic per-tjenesteområde breakdown (pie + % table, stacked bars over time, fylke/land comparison, table)
// averages: { fylkeName, fylke: [{year, kommune_count, share}], landName, land: [{year, share}] }
const BreakdownSection = ({ data, section, sumKey, title, subtitle, sumLabel, icon: Icon, testId, sourceTable, sourceNote, averages }) => {
  const rows = (data || []).filter((d) => d[section]);
  const latest = rows[rows.length - 1];
  const latestSum = latest?.[section][sumKey] || 0;
  const positiveSum = latest ? INVESTMENT_FIELDS.reduce((s, f) => s + Math.max(latest[section][f.key] || 0, 0), 0) : 0;
  const fylkeLatest = latest ? averages?.fylke?.find((d) => d.year === latest.year) : null;
  const landLatest = latest ? averages?.land?.find((d) => d.year === latest.year) : null;

  const shareRows = latest
    ? INVESTMENT_FIELDS.map((f) => ({
        key: f.key,
        name: f.label,
        value: latest[section][f.key] || 0,
        share: positiveSum ? (Math.max(latest[section][f.key] || 0, 0) / positiveSum) * 100 : 0,
        fylkeShare: fylkeLatest?.share?.[f.key] ?? null,
        landShare: landLatest?.share?.[f.key] ?? null,
        color: f.color,
      })).sort((a, b) => b.value - a.value)
    : [];
  const pieData = shareRows.filter((d) => d.value > 0);

  const chartData = rows.map((d) => {
    const point = { year: d.year, _sum: d[section][sumKey] };
    INVESTMENT_FIELDS.forEach((f) => { point[f.label] = Math.max(d[section][f.key] ?? 0, 0); });
    return point;
  });

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid={`${testId}-section`}>
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#103766]" />
          {title}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">{subtitle}</p>
      </CardHeader>
      <CardContent className="pt-6">
        {rows.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-[#8AA4BF]" data-testid={`${testId}-empty`}>
            Ingen data tilgjengeleg – klikk «Oppdater data»
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              <div className="xl:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                  Fordeling {latest.year}
                </p>
                <p className="text-sm text-[#4B647F] mb-2">{sumLabel}: {fmt(latestSum)} tusen kr</p>
                <div className="h-[260px]" data-testid={`${testId}-pie-chart`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={105} paddingAngle={1} stroke="white">
                        {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, name) => [`${fmt(v)} tusen kr (${pct(v, positiveSum)})`, name]} contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ShareTable rows={pieData} firstColumn="Tenesteområde" hasFylke={!!fylkeLatest} hasLand={!!landLatest} testId={testId} fylkeLabel={averages?.storby ? "Storbyane" : "Fylke"} />
                {(fylkeLatest || landLatest) && (
                  <p className="text-xs text-[#8AA4BF] mt-2">
                    {fylkeLatest && `${averages?.storby ? "Storbyane" : "Fylke"} = gjennomsnittleg andel for ${fylkeLatest.kommune_count} ${averages?.storby ? "storbyar" : "kommunar i fylket"}. `}
                    {landLatest && "Landet = alle kommunar samla (SSB-aggregat)."}
                  </p>
                )}
              </div>
              <div className="xl:col-span-3">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Utvikling over tid</p>
                <div className="h-[440px]" data-testid={`${testId}-chart`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      {endringsLinjer(chartData)}
                      <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                      <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                      <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={fmtAxis} />
                      <Tooltip
                        formatter={(v, name, item) => [`${fmt(v)} tusen kr (${pct(v, item.payload._sum)})`, name]}
                        contentStyle={tooltipStyle}
                        itemSorter={(item) => -item.value}
                        labelFormatter={(label, payload) => `${label} – Sum: ${fmt(payload?.[0]?.payload?._sum)} tusen kr`}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} itemSorter={null} />
                      {(shareRows.length ? shareRows : INVESTMENT_FIELDS.map((f) => ({ ...f, name: f.label }))).map((f) => (
                        <Bar key={f.key} dataKey={f.name} stackId="stk" fill={f.color} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {(fylkeLatest || landLatest) && (
              <ShareCompareChart
                rows={[...shareRows].sort((a, b) => (b.share + (b.fylkeShare || 0)) - (a.share + (a.fylkeShare || 0)))}
                fylkeName={fylkeLatest ? averages.fylkeName : null}
                snittOrd={averages?.storby ? "storbysnittet" : "fylkessnittet"}
                landName={landLatest ? averages.landName : null}
                year={latest.year}
                subject="andel"
                testId={testId}
                height={560}
              />
            )}

            <div className="overflow-x-auto">
              <Table data-testid={`${testId}-table`}>
                <TableHeader>
                  <TableRow className="bg-[#F6F8FB]">
                    <TableHead className="text-[#4B647F]">Hovudgruppe</TableHead>
                    {rows.map((d) => (
                      <TableHead key={d.year} className="text-[#4B647F] text-right">{d.year}</TableHead>
                    ))}
                    <TableHead className="text-[#4B647F] text-right">Andel {latest.year}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {INVESTMENT_FIELDS.map((f) => (
                    <TableRow key={f.key} className="hover:bg-[#F6F8FB]/50">
                      <TableCell className="text-sm">
                        <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: f.color }} />
                        {f.label}
                      </TableCell>
                      {rows.map((d) => (
                        <TableCell key={d.year} className="text-right text-sm">{fmt(d[section][f.key])}</TableCell>
                      ))}
                      <TableCell className="text-right text-sm text-[#4B647F] tabular-nums" data-testid={`${testId}-pct-${f.key}`}>
                        {pct(latest[section][f.key], positiveSum)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-[#F6F8FB] font-medium">
                    <TableCell className="text-sm">{sumLabel}</TableCell>
                    {rows.map((d) => (
                      <TableCell key={d.year} className="text-right text-sm" data-testid={`${testId}-sum-${d.year}`}>
                        {fmt(d[section][sumKey])}
                      </TableCell>
                    ))}
                    <TableCell className="text-right text-sm">100 %</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {sourceNote && <p className="text-xs text-[#8AA4BF] mt-2">{sourceNote}</p>}
            </div>
          </div>
        )}
        <p className="text-xs text-[#8AA4BF] mt-4 text-center">
          Kjelde:{" "}
          <a href={`https://www.ssb.no/statbank/table/${sourceTable}`} target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
            SSB {sourceTable} <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </CardContent>
    </Card>
  );
};

export default BreakdownSection;
